import { consumePendingNavigate } from './hermes/hermesNavigation.js';
import { observeSupervisorOwnership } from '../lib/runtime/supervisorBridge.js';
// Apolo IDE — AppShell (root layout when authenticated).
//
// Top bar with brand + tabs + user menu. Content area swaps based on the
// active workspace.

import { lazy, Suspense, useEffect, useState } from 'react';
import { Menu, Activity, Blocks, ChevronDown } from 'lucide-react';
import WorkbenchSurfaceTabs from './workbench/WorkbenchSurfaceTabs.jsx';
import WorkbenchPopover from './workbench/WorkbenchPopover.jsx';
import TopTabs from './TopTabs.jsx';
import AdaptersMenu from './AdaptersMenu.jsx';
import { useAgentStore } from '../store/useAgentStore.js';
import { useAuthStore } from '../store/useAuthStore.js';
import { useArtifactStore } from '../store/useArtifactStore.js';
import { useGlobalShortcuts } from '../hooks/useGlobalShortcuts.js';
import { apiFetch } from '../lib/api.js';
import { loadModelCatalog } from '../lib/modelCatalogClient.js';
import { startMemoryGuardian, readHeap } from '../lib/loop/memoryGuardian.js';
import { listRunningMeta, evictRun } from '../lib/agentRunStore.js';
import { ABORT_REASON } from '../lib/loop/abortReason.js';
import CodeView from './code/CodeView.jsx';
import SessionTabBar from './SessionTabBar.jsx';
import { sessionSurface } from '../lib/code/sessionSurface.js';
import PlanView from './plan/PlanView.jsx';
import ChatWorkspace from './chat/workspace/ChatWorkspace.jsx';
import CouncilView from './council/CouncilView.jsx';
import GodView from './god/GodView.jsx';
import RemoteDispatchView from './dispatch/RemoteDispatchView.jsx';
import SkillCommandPalette from './SkillCommandPalette.jsx';
import UpdateNotification from './UpdateNotification.jsx';
import UserStatusBar from './UserStatusBar.jsx';
import SchedulerToast from './SchedulerToast.jsx';
import DailyImproveModal from './DailyImproveModal.jsx';
import { useDailyImproveNews } from '../hooks/useDailyImproveNews.js';
import { useSessionTokenBridge } from '../hooks/useSessionTokenBridge.js';
import { useApoloLogo } from '../hooks/useApoloLogo.js';

// Global Claude-style artifact side panel. Lazy + only mounted while open so
// its heavy deps (Prism highlighter) stay out of the boot chunk.
const ArtifactPanel = lazy(() => import('./artifacts/ArtifactPanel.jsx'));
import { useLocalScheduler } from '../hooks/useLocalScheduler.js';
import { useRemoteRunner } from '../hooks/useRemoteRunner.js';
import { useCapabilityReporter } from '../hooks/useCapabilityReporter.js';
import { useLLMAdapters } from '../hooks/useLLMAdapters.js';
import { resolveBrain } from '../lib/brainRouter.js';
import { flushRunningAbortsNow } from '../lib/turnAbortBeacon.js';
import BackgroundRunsBar from './code/BackgroundRunsBar.jsx';
const ActivityPanel = lazy(() => import('./activity/ActivityPanel.jsx'));

// Versão do app — fonte única no package.json, injetada no build via
// vite.config (import.meta.env.VITE_APP_VERSION). A pill do header mostra o
// semver (v1.6.5); o tooltip mostra o full com codename (1.6.5-apolo-deus).
// Fallback vazio só é atingido se o build rodar sem o define (nunca em prod/dev).
const APP_VERSION_FULL = import.meta.env.VITE_APP_VERSION || '';
const APP_VERSION_SHORT = APP_VERSION_FULL.split('-')[0] || APP_VERSION_FULL;

// Resolve o cérebro (LLM) de um dispatch num run config pro runLocalAgentLoop.
// v2: cérebro flexível — default CLOUD (via Worker /agent-turn, sem Ollama),
// ou local conforme context.brain. A execução das tools continua local no Mac.
// Ver brainRouter.js + DECISIONS D-V2-002.
function prepareRemoteRun(dispatch) {
  const ctx = dispatch.context || {};
  const r = resolveBrain(ctx.brain, { dispatchId: dispatch.id });
  return {
    chatFn: r.chatFn,
    baseUrl: r.baseUrl,
    model: r.model,
    root: ctx.root || undefined,
    permissionsMode: dispatch.autonomy === 'auto' ? 'ignore' : 'ask',
  };
}

// Workspaces in tab order. Lazy + keep-alive (perf/RAM): a tab is NOT mounted
// until first activated; once mounted it STAYS mounted (hidden via display:none)
// so returning is instant — no re-fetch, full state + scroll preserved. The
// active tab uses display:contents, so its view is laid out exactly as a direct
// flex child (byte-identical to the old `active === x && <View/>`). Tabs you
// never open cost nothing; tabs you open stay warm forever.
const WORKSPACES = [
  ['code', CodeView],
  ['chat', ChatWorkspace],
  ['plan', PlanView],
  ['council', CouncilView],
  ['god', GodView],
  ['dispatch', RemoteDispatchView],
];

export default function AppShell() {
  useEffect(() => observeSupervisorOwnership(), []);
  const workspace = useAgentStore((s) => s.activeWorkspace);
  const [unified] = useState(() => { try { return localStorage.getItem('apolo:workbench:v2') !== '0'; } catch { return false; } });
  const active = unified && ['chat', 'plan'].includes(workspace) ? 'code' : workspace;
  // Lazy + keep-alive: remember every tab that has been activated. Each view is
  // mounted only after its first activation and then kept mounted forever (the
  // content area below hides inactive ones instead of unmounting them).
  const [mounted, setMounted] = useState(() => (active ? new Set([active]) : new Set()));
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    if (active) setMounted((m) => (m.has(active) ? m : new Set(m).add(active)));
  }, [active]);
  // P3.2 — toda sessão ATIVADA vira uma aba persistente (auto-add). Fica fora do
  // setActiveSession (que tem lógica de panes) pra não arriscar regressão de layout.
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const sessions = useAgentStore((s) => s.code.sessions);
  const draftContext = useAgentStore((s) => s.code.newChatContext);
  const draftScope = useAuthStore((s) => `${s.user?.id || 'anonymous'}:${s.activeProfileId || 'default'}`);
  const currentSession = sessions.find((s) => s.id === activeSessionId);
  const visibleDraft = draftContext?.scope === draftScope ? draftContext : null;
  const contextValue = currentSession ? currentSession.directory || currentSession.github_repo : visibleDraft?.directory || visibleDraft?.github_repo;
  const visibleContext = typeof contextValue === 'string' ? contextValue : contextValue?.full_name || contextValue?.name || '';
  const artifactsOpen = useArtifactStore((s) => s.open);
  const openSessionTab = useAgentStore((s) => s.openSessionTab);
  useEffect(() => {
    if (!activeSessionId) return;
    const sess = (sessions || []).find((x) => x.id === activeSessionId);
    if (sess) openSessionTab({ id: sess.id, kind: sessionSurface(sess), title: sess.name });
  }, [activeSessionId, sessions, openSessionTab]);
  const logo = useApoloLogo(64);
  useGlobalShortcuts();

  useEffect(() => {
    const open = (event) => { consumePendingNavigate(); setActivity({ runId: event.detail?.runId || null, section: event.detail?.section || 'runs' }); };
    const pending = consumePendingNavigate();
    if (pending) open({ detail: pending });
    window.addEventListener('apolo:openActivity', open);
    window.addEventListener('apolo:hermes:navigate', open);
    return () => { window.removeEventListener('apolo:openActivity', open); window.removeEventListener('apolo:hermes:navigate', open); };
  }, []);

  // Sprint v12 — scheduler local (Electron) executa tasks Ollama que o
  // Worker remoto não consegue alcançar. Polling 30s, só dispara com app
  // aberto. Detalhes: hooks/useLocalScheduler.js
  useLocalScheduler();

  // Sprint v13 — remote runner: roda tarefas disparadas do celular NESTA
  // máquina (app aberto), via o mesmo runLocalAgentLoop. Só liga no Electron
  // (precisa do IPC local). Detalhes: hooks/useRemoteRunner.js
  const remoteEnabled = typeof window !== 'undefined' && !!window.apolo;
  useRemoteRunner({ enabled: remoteEnabled, prepareRun: prepareRemoteRun });

  // Sprint v2 (E2) — o runner anuncia os cérebros disponíveis (cloud + Ollama)
  // pro celular mostrar status antes de disparar.
  const { adapters } = useLLMAdapters({ kind: 'text' });
  useCapabilityReporter({ enabled: remoteEnabled, adapters });
  // daily-auto-improve F4 — "What's New" pop-up after the 10am self-improve run.
  const dailyNews = useDailyImproveNews();
  // Bridge the JWT to ~/.apolo/session-token so the daily-improve cron can authenticate.
  useSessionTokenBridge();

  // Sprint v8.3 — sync adapter API keys into user_settings cache once per
  // session. chat.js looks for keys in user_settings (legacy) but new keys
  // live in llm_adapters (encrypted). Without this sync the chat throws
  // "[deepseek] missing DEEPSEEK_API_KEY" even when adapter is connected.
  useEffect(() => {
    apiFetch('/api/adapters/sync-all', { method: 'POST' }).catch(() => {});
  }, []);

  // E1-WS2-T03 — warm the server-driven model catalog once at boot (best-effort,
  // non-blocking). Caches it for resolveBaseUrl; a failure keeps the last good cache.
  useEffect(() => {
    loadModelCatalog().catch(() => {});
  }, []);

  // Onda 4 stage-1 (anti-travamento): supervisor de pressão de memória. Quando o
  // heap do renderer se aproxima do teto de 2GB (→ render-process-gone OOM, o
  // "Apolo travou"), aborta um run de BACKGROUND antes do crash — shed de carga em
  // vez de morrer a janela. NUNCA aborta o run observado. Kill-switch:
  // localStorage 'apolo:memGuardian:off' = '1'. Lógica testada em memoryGuardian.js.
  useEffect(() => {
    return startMemoryGuardian({
      getHeap: () => readHeap(),
      // item 2: pressão de RAM do SISTEMA (via IPC/vm_stat). Apolo cede carga
      // quando a MÁQUINA inteira está crítica — não só sob pressão do heap dele.
      getSystem: () => (window.apolo?.system?.memory ? window.apolo.system.memory() : null),
      getRunningMeta: listRunningMeta,
      shedRun: (id) => evictRun(id, ABORT_REASON.MEMORY_PRESSURE),
      onShed: (id, info) => console.warn(
        `[apolo] memory-guardian: run de fundo ${id} abortado — pressão de ${
          info?.reason === 'system' ? `RAM do sistema (${info.availableMB}MB livres)` : 'heap (OOM iminente)'
        }`,
      ),
      onWarn: (info) => console.warn(
        `[apolo] memory-guardian: pressão de ${
          info?.reason === 'system'
            ? `RAM do sistema (${info.availableMB}MB livres)`
            : `heap (${info?.usedMB}/${info?.limitMB}MB)`
        } — nenhum run de fundo p/ shedar`,
      ),
      isEnabled: () => {
        try { return localStorage.getItem('apolo:memGuardian:off') !== '1'; } catch { return true; }
      },
    });
  }, []);

  // Auditoria 2026-07-02 — no unload (reload/fechar app), reconcilia o status das
  // sessões com run viva: dispara turn-abort (keepalive) pra cada uma, senão o
  // servidor deixa a sessão "running" fantasma por 10min. pagehide é mais
  // confiável que beforeunload pra este flush.
  useEffect(() => {
    const onHide = () => { try { flushRunningAbortsNow(); } catch { /* noop */ } };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Sprint v9.4 — hamburger no header dispara evento que CodeView/ChatView
  // escutam pra abrir o drawer mobile. Single source of truth no header.
  function openMobileDrawer() {
    window.dispatchEvent(new CustomEvent('apolo:openMobileDrawer'));
  }

  return (
    // h-[100dvh] usa Dynamic Viewport Height — segue address bar do Safari
    // mobile (sem o jump de 100vh quando URL bar entra/sai). Fallback h-screen
    // pra browsers sem suporte.
    <div data-workbench={unified || undefined} className="flex h-screen h-[100dvh] w-full max-w-[100vw] flex-col overflow-x-clip bg-apolo-bg text-apolo-textPrimary">
      {/*
        Window drag region for Electron titleBarStyle: 'hiddenInset'.
        Whole header is draggable; interactive children opt out via
        WebkitAppRegion: 'no-drag'.
        Sticky top-0 z-40 + backdrop-blur — defensive em mobile pra garantir
        que o header NÃO sai de vista mesmo se algum descendant escapar do
        flex layout. backdrop-blur-md cria glass effect quando algo passa
        por baixo (raro mas polido).
      */}
      <header
        className="sticky top-0 z-40 flex shrink-0 flex-col border-b border-apolo-borderSubtle bg-apolo-header backdrop-blur-md"
        style={{
          WebkitAppRegion: 'drag',
          // Safe area pro notch iPhone (web/PWA contexts) — em standalone
          // Home Screen o status bar fica sobre o header, esse padding move
          // o conteúdo pra baixo do notch.
          paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
        }}
      >
        {/* Linha 1: brand (esq) + abas no DESKTOP (centro) + adapters (dir). */}
        <div className="workbench-titlebar grid grid-cols-[auto_1fr_auto] items-center gap-2 px-3 pb-2 sm:gap-4 sm:px-4 lg:pl-20">
          {/* Col 1: brand (left) */}
          <div className="flex items-center gap-2" style={{ WebkitAppRegion: 'no-drag' }}>
            {/* Hamburger mobile — abre drawer de cada view. lg:hidden = desktop usa sidebar inline. */}
            <button
              type="button"
              onClick={openMobileDrawer}
              aria-label="Abrir menu lateral"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 lg:hidden"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <img
              src={logo.src}
              srcSet={logo.srcSet}
              alt=""
              aria-hidden="true"
              width={20}
              height={20}
              className="h-5 w-5 select-none object-contain"
              style={{ filter: 'var(--apolo-logo-glow)' }}
              draggable={false}
            />
            <span className="hidden text-sm font-semibold tracking-tight sm:inline">Apolo</span>
            {/* Pill de versão — pequena, amarela (pedido do Pedro). Deriva do
                package.json via VITE_APP_VERSION (fonte única): pill mostra o
                semver, tooltip mostra o codename completo. */}
            <span
              className="hidden select-none items-center rounded-full border px-1.5 py-px font-mono text-[9px] font-semibold leading-none tracking-tight sm:inline-flex"
              style={{ color: '#f2c94c', borderColor: 'rgba(242,201,76,.38)', background: 'rgba(242,201,76,.12)' }}
              title={`Apolo v${APP_VERSION_FULL}`}
            >
              v{APP_VERSION_SHORT}
            </span>
          </div>
          {/* Col 2: abas no pill, na MESMA linha do header (mobile e desktop).
           * No mobile a coluna 1fr ESTICA (justify-self-stretch + min-w-0) pra as
           * abas rolarem horizontal DENTRO do track (arrasta pro lado p/ escolher);
           * no desktop (lg+) ficam centradas. UMA instância de TopTabs. */}
          <div className="min-w-0 justify-self-stretch lg:justify-self-center" style={{ WebkitAppRegion: 'no-drag' }}>
            {unified ? <div className="workbench-title-context">
              <WorkbenchSurfaceTabs />
              <span className="workbench-current-title" title={currentSession?.name}>{currentSession?.name || 'Novo chat'}</span>
              {visibleContext && <small title={visibleContext}>{visibleContext.split('/').filter(Boolean).pop()}</small>}
            </div> : <TopTabs />}
          </div>
          {/* Col 3: Adapters global (canto superior direito, sempre visível em
           * todas as tabs). Dropdown ancorado com todos os provedores + API
           * keys inline + status GitHub. Self-contained (estado + ⌘E dentro). */}
          <div className="flex items-center justify-end" style={{ WebkitAppRegion: 'no-drag' }}>
            <button aria-label="Atividade e configurações" className="inline-flex h-11 w-11 items-center justify-center rounded-lg" onClick={() => setActivity({ section: 'runs' })}><Activity size={18} /></button>
            {unified && <WorkbenchPopover label="Ferramentas avançadas" className="workbench-tools-trigger" trigger={<><Blocks size={16} /><span>Ferramentas</span><ChevronDown size={12} /></>}>
              {(close) => <>{[['code', 'Chats', 'Conversar, planejar e construir'], ['council', 'Council', 'Comparar perspectivas dos modelos'], ['god', 'GOD', 'Orquestração avançada'], ['dispatch', 'Dispatch', 'Execução remota']].map(([id, name, description]) => <button key={id} className="workbench-menu-item" onClick={() => { useAgentStore.getState().setActiveWorkspace(id); close(); }}><span><strong>{name}</strong><small>{description}</small></span></button>)}</>}
            </WorkbenchPopover>}
            <AdaptersMenu />
          </div>
        </div>
      </header>

      {/* P3.2 — abas de sessão persistentes (cross Code/Chat), entre o header e o
       * conteúdo. Retorna null quando não há sessão aberta ou a surface não é code/chat. */}
      {!unified && <SessionTabBar />}

      <div className="flex flex-1 overflow-hidden">
        {WORKSPACES.map(([key, View]) =>
          mounted.has(key) && !(unified && ['chat', 'plan'].includes(key)) ? (
            <div key={key} style={{ display: active === key ? 'contents' : 'none' }}>
              <View unified={unified} />
            </div>
          ) : null,
        )}
      </div>
      {/* Claude-style artifacts: global side panel, mounted only while an
       * artifact is open (keeps its Prism chunk out of boot). Overlays both
       * Chat and Code surfaces. */}
      {artifactsOpen && (
        <Suspense fallback={null}>
          <ArtifactPanel />
        </Suspense>
      )}
      {activity && (
        <Suspense fallback={null}>
          <ActivityPanel section={activity.section} runId={activity.runId} onClose={() => setActivity(null)} />
        </Suspense>
      )}
      {/* Sprint v8 C2 — global Command Palette (⌘K). */}
      <SkillCommandPalette />
      {/* Sprint v8 C7 — autoUpdater toast (only renders when IPC fires). */}
      <UpdateNotification />
      {/* Sprint v9.13 — user-self anchor flutuante bottom-left global (Linear/
       * Notion/Slack pattern). Visível em TODAS as tabs. Mobile cai pro
       * hamburger drawer. */}
      {(!unified || active !== 'code') && <UserStatusBar />}
      {/* Auditoria 2026-07-02 — barra global de runs em segundo plano: quando você
       * troca de perfil com um agente rodando, a sessão sai da lista (escopo
       * estrito por perfil) mas o run CONTINUA. Esta barra o mantém visível +
       * parável + com "Abrir" (volta pro perfil da sessão). */}
      <BackgroundRunsBar />
      {/* Sprint v12 — toast pro local scheduler (Ollama tasks executadas
       * dentro do app). Escuta `apolo:toast` global, empilha até 4. */}
      <SchedulerToast />
      {/* daily-auto-improve F4 — "What's New" pop-up after the 10am self-improve run. */}
      <DailyImproveModal open={dailyNews.open} report={dailyNews.report} onClose={dailyNews.dismiss} />
    </div>
  );
}
